import { Box, Button, SwipeableDrawer, Typography } from '@mui/material';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import CredentialService from 'services/api/CredentialService';
import { useSnackbar } from 'notistack';
import craftProStamp from 'assets/images/craftPro/craftProCredCardStamp.svg';

import commonStyles from 'styles/common.module.scss';
import styles from 'components/credentials/CompletionCard/CompletionCard.module.scss';
import overlayStyles from 'components/credentials/BadgeImage/BadgeImage.module.scss';
import craftProStyles from './CraftProCard.module.scss';
import moment from 'moment';
import routes from 'store/configs/Routes';
import { Completion } from 'store/types/Completions';
import type { Credential } from 'store/types/Credentials';
import { getTypeSafeItem } from 'util/Credentials';
import { TypeSafeCredentialDetails } from 'store/types/TypeSafeCredentialDetails';
import Spinner from 'components/shared/Spinner';
import { useWindowSize } from 'util/Window';
import { Puller } from 'util/Layout';

interface CraftProCardProps {
  item: Credential | Completion;
  onSubmit?: (documentLink: string) => void | Promise<void>;
  showContentType?: boolean;
  isPublic?: boolean;
}

const CraftProCard: React.FunctionComponent<CraftProCardProps> = ({
  item,
  onSubmit,
  showContentType = true,
  isPublic = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const { isMobile } = useWindowSize();
  const typeSafeItem: TypeSafeCredentialDetails = getTypeSafeItem(item);
  const docLink = typeSafeItem.Document?.Link;
  const detailLink = isPublic
    ? routes.publicBadgeDetails + '?id=' + typeSafeItem.CompletionId + '&iscp=true'
    : routes.badgeDetails + '?id=' + typeSafeItem.CompletionId + '&iscp=true';

  const toggleDrawer = (newOpen: boolean) => () => {
    isMobile && docLink && setOpenDrawer(newOpen);
  };

  const handlePrintClick = useCallback(() => {
    if (onSubmit && docLink) {
      onSubmit(docLink);
      return;
    }

    setLoading(true);
    CredentialService.getDocumentLink(docLink)
      .then((response) => {
        setLoading(false);
        window.location.assign(response);
      })
      .catch((error) => {
        setLoading(false);
        enqueueSnackbar(error?.body, { variant: 'error' });
      });
  }, [docLink, enqueueSnackbar, onSubmit]);

  return (
    <Spinner loading={loading} fullPage={false} className={craftProStyles.cardWrapper}>
      <div
        onClick={toggleDrawer(true)}
        className={classNames(commonStyles.contentCard, styles.credentialCard, overlayStyles.overlayContainer)}
      >
        {typeSafeItem.CraftProShowBadge && typeSafeItem.CertificateLogoUrl && (
          <img src={typeSafeItem.CertificateLogoUrl} className={styles.cardBadge} />
        )}
        <Typography className={styles.cardTitle} variant={'body1'}>
          {!typeSafeItem.CraftProShowBadge && <img src={craftProStamp} className={styles.stamp} />}
          {typeSafeItem.Name}
        </Typography>
        {typeSafeItem.Date && (
          <Typography variant={'body2'}>Date Earned: {moment(typeSafeItem.Date).format('L')}</Typography>
        )}
        {typeSafeItem.ExpireDate && (
          <Typography variant={'body2'}>Expiration Date: {moment(typeSafeItem.ExpireDate).format('L')}</Typography>
        )}
        {typeSafeItem.ContentFamily && <Typography variant={'body2'}>{typeSafeItem.ContentFamily}</Typography>}
        {!isMobile && docLink && (
          <Box className={overlayStyles.hiddenOverlay}>
            <Button onClick={handlePrintClick} className={overlayStyles.downloadLink}>
              Print Credential
            </Button>
            {typeSafeItem.Date && (
              <p className={overlayStyles.viewDetails}>Date Earned: {moment(typeSafeItem.Date).format('L')}</p>
            )}
          </Box>
        )}
      </div>
      {isMobile && (
        <SwipeableDrawer
          open={openDrawer}
          onOpen={toggleDrawer(true)}
          onClose={toggleDrawer(false)}
          anchor={'bottom'}
          swipeAreaWidth={56}
          ModalProps={{
            keepMounted: false,
          }}
        >
          <div className={styles.drawerWrapper}>
            <Puller />
            {docLink && (
              <Typography
                onClick={handlePrintClick}
                className={classNames(styles.drawerOption, styles.mobileDownloadLink)}
              >
                {'Print Credential'}
              </Typography>
            )}
          </div>
        </SwipeableDrawer>
      )}
    </Spinner>
  );
};
export default CraftProCard;
